import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import ConnectionStatus from './ConnectionStatus.jsx'
import { HotTable } from '@handsontable/react-wrapper'
import { registerAllModules } from 'handsontable/registry'
import { textRenderer } from 'handsontable/renderers'
import 'handsontable/styles/handsontable.css'
import 'handsontable/styles/ht-theme-horizon.css'
import { cable } from '../../cable.js'
import { t } from '../../i18n.js'
import { buildGridRows } from '../report/buildReportRows.js'
import { fetchPlan } from '../report/fetchPlan.js'
import { cellMeta, rowHeight } from '../report/reportCellMeta.js'
import { HOT_LICENSE_KEY } from '../utils/handsontableLicense.js'
import { sanitizeHandsontableHtml } from '../utils/handsontableSanitizer.js'

registerAllModules()

// Debounce delay for real-time refetch after upstream changes (ms)
const REALTIME_DEBOUNCE = 1500

// EVA-274: the simplified, client-facing Cash Flow report.
//
// Presentation only. It fetches the Cash Flow Audit tab's JSON verbatim and
// changes nothing but row selection, ordering, suppression, formatting and
// styling (spec §8) — every number comes from the engine already summed. All row
// structure lives in REPORT_SPEC; this component only wires it to Handsontable.
export default function CashFlowReport({ valuationId, cashFlowUrl, translations }) {
  const hotRef = useRef(null)
  const fetchRef = useRef(null)
  // What the payload in state represents ({ view, detailed }), null until the
  // first response lands. Read by fetchPlan; written only from the response's own
  // echo, so it can never claim detail the payload does not carry.
  const heldRef = useRef(null)
  // Monotonic request id, and the id of the newest response already written into
  // state. A response is applied unless it is genuinely OLDER than what is on
  // screen — "a newer request is in flight" is not a reason to throw away data we
  // asked for and received, or a slow upgrade racing a realtime refetch would
  // leave `held` claiming no detail while the carets sit open onto nothing.
  const requestSeqRef = useRef(0)
  const appliedSeqRef = useRef(0)
  // Spec §2 / decision #4: annual by default (the Audit tab starts monthly).
  const [view, setView] = useState('annual')
  const [expandedKeys, setExpandedKeys] = useState(() => new Set())
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [connectionStatus, setConnectionStatus] = useState('connecting')
  const [connectionMountEl, setConnectionMountEl] = useState(null)
  const [periodEnds, setPeriodEnds] = useState([])
  const [rows, setRows] = useState({})

  // Derived, not latched: collapsing every row returns the NEXT realtime refetch
  // to the small payload instead of carrying tenant arrays for the rest of the
  // session. Collapsing does not itself refetch — fetchPlan keeps the detailed
  // payload already in hand, since it is a superset of the small one.
  const detailed = expandedKeys.size > 0

  // Mount the connection badge into the server-rendered card header.
  useEffect(() => {
    const container = document.querySelector('[data-react-component-component-value="CashFlowReport"]')
    const titleEl = container?.closest('.card')?.querySelector('.card-header h5.card-title')
    if (!titleEl) return undefined

    const mount = document.createElement('span')
    mount.className = 'tenants-connection-status-mount'
    titleEl.appendChild(mount)
    setConnectionMountEl(mount)

    return () => { setConnectionMountEl(null); mount.remove() }
  }, [])

  // `spinner` tears the grid down while the request is in flight; `reportErrors`
  // decides whether a failure reaches the user. They are separate questions — the
  // detail upgrade must not blank the table but must still be able to say it
  // failed, whereas a background realtime refetch stays quiet on both counts.
  const fetchCashFlow = useCallback(async ({ spinner = true, reportErrors = true } = {}) => {
    if (spinner) setLoading(true)
    if (reportErrors) setError(null)

    const seq = (requestSeqRef.current += 1)

    try {
      const url = new URL(cashFlowUrl, window.location.origin)
      url.searchParams.set('view', view)
      url.searchParams.set('detailed', detailed ? 'true' : 'false')

      const res = await fetch(url.toString(), { headers: { Accept: 'application/json' } })
      const json = await res.json()
      if (!res.ok || !json.success) throw new Error(json.error || `Request failed (${res.status})`)
      if (seq < appliedSeqRef.current) return
      appliedSeqRef.current = seq

      // Written together, under one guard, in one commit: `held` can never
      // describe a payload other than the one now in `rows` / `periodEnds`.
      const data = json.data || {}
      heldRef.current = { view: data.view, detailed: Boolean(data.detailed) }
      setPeriodEnds(data.period_ends || [])
      setRows(data.rows || {})
      setError(null)
    } catch (e) {
      if (seq < appliedSeqRef.current) return
      if (reportErrors) setError(e?.message || t('error_fallback', translations))
    } finally {
      if (spinner) setLoading(false)
    }
  }, [cashFlowUrl, view, detailed, translations])

  useEffect(() => { fetchRef.current = fetchCashFlow }, [fetchCashFlow])

  // EVA-275: `detailed` is derived from the expanded row set, so it flips as soon
  // as the first caret opens. Routing that through fetchPlan is what stops the
  // flip from blanking a table whose numbers are still correct — and stops
  // collapsing the last row from refetching at all.
  //
  // `view` and `detailed` are redundant with `fetchCashFlow`'s identity today;
  // keep them, they state the intent. `expandedKeys` is NOT redundant and is the
  // recovery path: if an upgrade fails, `detailed` stays true and nothing else
  // would ever re-run this effect, so the next caret click retries it. In the
  // steady state fetchPlan answers null and the extra runs cost nothing.
  useEffect(() => {
    const plan = fetchPlan({ held: heldRef.current, view, detailed })
    if (plan) fetchCashFlow({ spinner: plan.spinner })
  }, [fetchCashFlow, view, detailed, expandedKeys])

  // Shared ValuationChannel — fires on Tenant / MLA / Growth changes.
  useEffect(() => {
    if (!valuationId) return undefined
    let debounceTimer = null

    const subscription = cable.subscriptions.create(
      { channel: 'ValuationChannel', valuation_id: valuationId },
      {
        connected() { setConnectionStatus('connected') },
        disconnected() { setConnectionStatus('disconnected') },
        received() {
          if (debounceTimer) clearTimeout(debounceTimer)
          debounceTimer = setTimeout(
            () => { fetchRef.current?.({ spinner: false, reportErrors: false }) },
            REALTIME_DEBOUNCE
          )
        }
      }
    )

    return () => {
      subscription.unsubscribe()
      if (debounceTimer) clearTimeout(debounceTimer)
    }
  }, [valuationId])

  const labelOf = useCallback(
    (key, vars) => t(key, translations, vars || {}),
    [translations]
  )

  const gridRows = useMemo(
    () => buildGridRows({ rows, periodEnds, view, expandedKeys, detailed, labelOf }),
    [rows, periodEnds, view, expandedKeys, detailed, labelOf]
  )

  const data = useMemo(() => gridRows.map((row) => row.cells), [gridRows])

  const toggleExpanded = useCallback((key) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }, [])

  // The caret. Built with createElement/textContent, never innerHTML — OpEx and
  // CapEx row labels are user-entered.
  const expanderRenderer = useCallback((instance, td, row, col, prop, value, cellProperties) => {
    textRenderer(instance, td, row, col, prop, '', cellProperties)
    td.textContent = ''

    const caret = document.createElement('span')
    caret.className = 'cfr-caret'
    caret.textContent = cellProperties.cfrExpanded ? '▾' : '▸'
    caret.setAttribute('role', 'button')
    caret.setAttribute('tabindex', '0')
    caret.setAttribute('aria-expanded', cellProperties.cfrExpanded ? 'true' : 'false')
    caret.setAttribute(
      'aria-label',
      t(cellProperties.cfrExpanded ? 'expand.hide' : 'expand.show', translations)
    )

    const label = document.createElement('span')
    label.className = 'cfr-caret-label'
    label.textContent = value == null ? '' : String(value)

    td.appendChild(caret)
    td.appendChild(label)
  }, [translations])

  // These close over `gridRows` directly rather than reading it from a ref: the
  // grid mounts before the parent's effects run, so a ref is still empty when
  // Handsontable first asks for cell meta — and its meta is memoized per render
  // cycle, so the empty first answer would stick. Passing a new function
  // identity is what makes the React wrapper call updateSettings, which clears
  // that cache.
  const cellsFn = useCallback(
    (row, col) => cellMeta(gridRows[row], col, { textRenderer, expanderRenderer }),
    [gridRows, expanderRenderer]
  )

  const rowHeightsFn = useCallback((row) => rowHeight(gridRows[row]), [gridRows])

  const onCellMouseDown = useCallback((event, coords) => {
    if (!coords || coords.col !== 0 || coords.row < 0) return
    const row = gridRows[coords.row]
    if (row?.expandable) toggleExpanded(row.key)
  }, [gridRows, toggleExpanded])

  // Expose the instance for end-to-end specs (mirrors __evaCashFlowHot).
  useEffect(() => {
    if (typeof window !== 'undefined' && !loading) {
      window.__evaCashFlowReportHot = hotRef.current?.hotInstance
    }
    return () => {
      if (typeof window !== 'undefined') delete window.__evaCashFlowReportHot
    }
  })

  return (
    <div className="cash-flow-report-container">
      <div className="cash-flow-report-toolbar">
        <div className="btn-group" role="group" aria-label={t('view.aria_label', translations)}>
          {['monthly', 'quarterly', 'annual'].map((frequency) => (
            <button
              key={frequency}
              type="button"
              className={`btn btn-sm ${view === frequency ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setView(frequency)}
            >
              {t(`view.${frequency}`, translations)}
            </button>
          ))}
        </div>
      </div>

      {error && <div className="alert alert-danger mb-2">{error}</div>}

      {loading ? (
        <div className="d-flex justify-content-center py-4">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">{t('loading', translations)}</span>
          </div>
        </div>
      ) : (
        <div className="cash-flow-report-viewport">
          <HotTable
            ref={hotRef}
            data={data}
            rowHeaders={false}
            colHeaders={false}
            // §2: both header rows stay pinned vertically, the row-label column
            // horizontally. Walkontable renders the top-left corner overlay when
            // both are set, so the header labels stay pinned on both axes.
            fixedRowsTop={2}
            fixedColumnsStart={1}
            themeName="ht-theme-horizon"
            licenseKey={HOT_LICENSE_KEY}
            sanitizer={sanitizeHandsontableHtml}
            // Annual fits the card, so let it fill; the finer views overflow
            // anyway and stretching would fight the horizontal scroll.
            stretchH={view === 'annual' ? 'all' : 'none'}
            colWidths={(index) => (index === 0 ? 300 : 110)}
            width="100%"
            height="70vh"
            readOnly={true}
            manualColumnResize={true}
            manualRowResize={false}
            rowHeights={rowHeightsFn}
            cells={cellsFn}
            afterOnCellMouseDown={onCellMouseDown}
          />
        </div>
      )}

      <ConnectionStatus
        status={connectionStatus}
        mountEl={connectionMountEl}
        labels={{
          connected: t('connection.live', translations),
          disconnected: t('connection.disconnected', translations),
          connecting: t('connection.connecting', translations)
        }}
      />
    </div>
  )
}
